<template>
  <v-container
    id="approval"
    fluid
    class="dashboard-container"
    style="padding: 24px 96px; min-height: 100vh;"
  >
    <div class="mb-6">
      <h2 class="text-h5 text--primary font-weight-bold d-flex align-center">
        <v-icon color="#4FC3F7" class="mr-3" size="32">mdi-clipboard-check-outline</v-icon>
        Aprovações
      </h2>
      <div class="text-body-2 text--secondary">
        {{ $t('approval.subtitle') }}
      </div>
    </div>

    <v-tabs
      v-model="activeTab"
      background-color="transparent"
      class="approval-tabs mb-6"
    >
      <v-tab>
        <v-icon left>mdi-clipboard-text</v-icon>
        PDIs
      </v-tab>
      <v-tab>
        <v-icon left>mdi-account-group</v-icon>
        One-on-One
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" style="background: transparent !important;">
      
      <v-tab-item>
        <v-row class="mb-4">
          <v-col cols="12" md="3">
            <v-card class="pa-4 custom-card" flat>
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <div class="text-caption text--secondary">{{ $t('approval.pendingApproval') }}</div>
                  <div class="text-h3 orange--text font-weight-bold">
                    {{ pendingCount }}
                  </div>
                </div>
                <v-icon size="50" color="orange">mdi-clock-alert-outline</v-icon>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card class="pa-4 custom-card" flat>
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <div class="text-caption text--secondary">{{ $t('approval.pendingCompletion') }}</div>
                  <div class="text-h3 blue--text font-weight-bold">
                    {{ pendingCompletionCount }}
                  </div>
                </div>
                <v-icon size="50" color="blue">mdi-clipboard-check-outline</v-icon>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card class="pa-4 custom-card" flat>
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <div class="text-caption text--secondary">Aprovados</div>
                  <div class="text-h3 success--text font-weight-bold">
                    {{ approvedCount }}
                  </div>
                </div>
                <v-icon size="50" color="success">mdi-check-circle-outline</v-icon>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card class="pa-4 custom-card" flat>
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <div class="text-caption text--secondary">Rejeitados</div>
                  <div class="text-h3 error--text font-weight-bold">
                    {{ rejectedCount }}
                  </div>
                </div>
                <v-icon size="50" color="error">mdi-close-circle-outline</v-icon>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-chip-group v-model="statusFilter" mandatory active-class="cyan--text text--lighten-1" class="mb-4">
          <v-chip value="all" outlined class="filter-chip">
            <v-icon small left>mdi-format-list-bulleted</v-icon>
            Todos
          </v-chip>
          <v-chip value="PENDING_APPROVAL" outlined class="filter-chip">
            <v-icon small left>mdi-clock-alert</v-icon>
            Pendentes de Aprovação
          </v-chip>
          <v-chip value="PENDING_COMPLETION" outlined class="filter-chip">
            <v-icon small left>mdi-clipboard-check</v-icon>
            Pendentes de Conclusão
          </v-chip>
          <v-chip value="APPROVED" outlined class="filter-chip">
            <v-icon small left>mdi-check-circle</v-icon>
            Aprovados
          </v-chip>
          <v-chip value="REJECTED" outlined class="filter-chip">
            <v-icon small left>mdi-close-circle</v-icon>
            Rejeitados
          </v-chip>
        </v-chip-group>

        <v-row>
          <v-col cols="12">
            <v-card
              v-for="pdi in filteredPdis"
              :key="pdi.id"
              class="mb-4 custom-card elevation-0"
              :class="getPdiCardClass(pdi.status, pdi.progress)"
            >
              <v-card-text class="pa-5">
                <v-row align="center">
                  <v-col cols="auto">
                    <v-avatar
                      :color="pdi.avatarUser ? 'transparent' : getAvatarColor(pdi.userName || 'User')"
                      size="60"
                      class="elevation-2"
                    >
                      <v-img
                        v-if="pdi.avatarUser"
                        :src="pdi.avatarUser"
                        :alt="pdi.userName"
                      />
                      <span v-else class="white--text text-h6">
                        {{ getInitials(pdi.userName || 'U') }}
                      </span>
                    </v-avatar>
                  </v-col>

                  <v-col>
                    <div class="d-flex align-center mb-2">
                      <h3 class="text-h6 font-weight-bold text--primary mr-3 mb-0">
                        {{ pdi.title }}
                      </h3>
                      <v-chip
                        :color="getStatusColor(pdi.status)"
                        small
                        dark
                        label
                        outlined
                        style="border-width: 1px;"
                      >
                        <v-icon small left>{{ getStatusIcon(pdi.status) }}</v-icon>
                        {{ getStatusLabel(pdi.status) }}
                      </v-chip>
                    </div>

                    <div class="d-flex align-center mb-2">
                      <v-chip
                        x-small
                        color="rgba(79, 195, 247, 0.1)"
                        text-color="#4FC3F7"
                        class="mr-2"
                      >
                        <v-icon x-small left>mdi-account</v-icon>
                        {{ pdi.userName || 'Usuário' }}
                      </v-chip>
                      <v-chip
                        v-if="pdi.category"
                        x-small
                        outlined
                        class="mr-2 category-chip"
                      >
                        <v-icon x-small left>mdi-tag</v-icon>
                        {{ pdi.category }}
                      </v-chip>
                      <span class="text-caption text--secondary">
                        <v-icon x-small class="text--secondary mr-1">mdi-calendar</v-icon>
                        Prazo: {{ formatDate(pdi.deadline) }}
                      </span>
                      <span class="text-caption ml-3 text--secondary">
                        <v-icon x-small class="text--secondary mr-1">mdi-target</v-icon>
                        {{ pdi.goals ? pdi.goals.length : 0 }} meta(s)
                      </span>
                    </div>

                    <div v-if="pdi.status === 'IN_PROGRESS'" class="mt-3">
                      <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-caption text--secondary">Progresso</span>
                        <span class="text-caption font-weight-bold" :class="pdi.progress >= 100 ? 'success--text' : 'cyan--text'">
                          {{ pdi.progress || 0 }}%
                        </span>
                      </div>
                      <v-progress-linear
                        :value="pdi.progress || 0"
                        :color="pdi.progress >= 100 ? 'success' : '#4FC3F7'"
                        height="6"
                        rounded
                      />
                    </div>
                  </v-col>

                  <v-col cols="auto" class="text-right" v-if="pdi.status === 'PENDING_APPROVAL'">
                    <v-btn
                      color="success"
                      small
                      dark
                      @click="openApproveDialog(pdi)"
                      :loading="saving === pdi.id"
                      class="mr-2 text-none"
                    >
                      <v-icon small left>mdi-check</v-icon>
                      Aprovar
                    </v-btn>
                    <v-btn
                      color="error"
                      small
                      outlined
                      @click="openRejectDialog(pdi)"
                      :disabled="saving === pdi.id"
                      class="text-none"
                    >
                      <v-icon small left>mdi-close</v-icon>
                      Rejeitar
                    </v-btn>
                  </v-col>

                  <v-col cols="auto" class="text-right" v-if="pdi.status === 'IN_PROGRESS' && pdi.progress >= 100">
                    <v-btn
                      color="#4FC3F7"
                      dark
                      small
                      @click="openCompletePdiDialog(pdi)"
                      :loading="saving === pdi.id"
                      class="text-none"
                    >
                      <v-icon small left>mdi-check-all</v-icon>
                      Concluir PDI
                    </v-btn>
                  </v-col>

                  <v-col cols="auto">
                    <v-btn
                      icon
                      class="text--secondary"
                      @click="toggleExpand(pdi.id)"
                    >
                      <v-icon>{{ expandedPdis.includes(pdi.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-expand-transition>
                  <div v-if="expandedPdis.includes(pdi.id)" class="mt-4 pt-4 divider-line">
                    <div class="mb-4">
                      <div class="text-subtitle-2 font-weight-bold mb-1 text--primary">Descrição:</div>
                      <p class="text-body-2 text--secondary">
                        {{ pdi.description || 'Sem descrição disponível.' }}
                      </p>
                    </div>

                    <v-row class="mb-4">
                      <v-col cols="12" md="6">
                        <v-card flat class="pa-3 info-box">
                          <div class="text-caption text--secondary">Criado por</div>
                          <div class="font-weight-bold text--primary">{{ pdi.userName || 'Usuário' }}</div>
                        </v-card>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-card flat class="pa-3 info-box">
                          <div class="text-caption text--secondary">Data de Criação</div>
                          <div class="font-weight-bold text--primary">{{ formatDate(pdi.createdAt) }}</div>
                        </v-card>
                      </v-col>
                    </v-row>

                    <div v-if="pdi.goals && pdi.goals.length > 0" class="mb-4">
                      <div class="text-subtitle-2 font-weight-bold mb-2 text--primary">Metas do PDI:</div>
                      <v-list dense class="transparent pa-0">
                        <v-list-item
                          v-for="(goal, index) in pdi.goals"
                          :key="index"
                          class="px-0"
                        >
                          <v-list-item-icon class="mr-3">
                            <v-icon color="#4FC3F7" small>mdi-target</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="text--primary font-weight-medium">
                              {{ goal.title }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </div>

                    <v-alert
                      v-if="pdi.status === 'REJECTED' && pdi.rejectionReason"
                      type="error"
                      outlined
                      dense
                      class="mb-3"
                    >
                      <div class="text-subtitle-2 font-weight-bold mb-1">
                        Motivo da Rejeição:
                      </div>
                      <div class="text-body-2">{{ pdi.rejectionReason }}</div>
                    </v-alert>

                    <div v-if="pdi.status === 'PENDING_APPROVAL'" class="d-flex justify-end mt-4">
                      <v-btn
                        color="success"
                        dark
                        @click="openApproveDialog(pdi)"
                        :loading="saving === pdi.id"
                        class="mr-3 text-none"
                      >
                        <v-icon left>mdi-check-circle</v-icon>
                        Aprovar PDI
                      </v-btn>
                      <v-btn
                        color="error"
                        outlined
                        @click="openRejectDialog(pdi)"
                        :disabled="saving === pdi.id"
                        class="text-none"
                      >
                        <v-icon left>mdi-close-circle</v-icon>
                        Rejeitar PDI
                      </v-btn>
                    </div>

                    <div v-if="pdi.status === 'IN_PROGRESS' && pdi.progress >= 100" class="d-flex justify-space-between align-center mt-4">
                      <v-alert
                        type="info"
                        color="rgba(79, 195, 247, 0.15)"
                        text-color="#4FC3F7"
                        dense
                        class="mb-0 flex-grow-1 mr-4 border-none"
                      >
                        <span class="text--primary font-weight-medium"><v-icon small color="#4FC3F7" class="mr-2">mdi-check-all</v-icon>Todas as metas foram concluídas! Você pode finalizar este PDI.</span>
                      </v-alert>
                      <v-btn
                        color="#4FC3F7"
                        dark
                        @click="openCompletePdiDialog(pdi)"
                        :loading="saving === pdi.id"
                        class="text-none"
                      >
                        <v-icon left>mdi-check-all</v-icon>
                        Concluir PDI
                      </v-btn>
                    </div>

                    <v-alert
                      v-if="pdi.status === 'APPROVED'"
                      type="success"
                      outlined
                      dense
                      class="mt-3"
                    >
                      PDI aprovado em {{ formatDate(pdi.updatedAt) }}
                    </v-alert>
                  </div>
                </v-expand-transition>
              </v-card-text>
            </v-card>

            <v-card v-if="filteredPdis.length === 0 && !loading" class="pa-8 text-center custom-card elevation-0">
              <v-icon size="80" class="empty-icon">mdi-clipboard-check-outline</v-icon>
              <h3 class="text-h6 text--primary mt-4">
                {{ statusFilter === 'PENDING_APPROVAL' ? 'Nenhum PDI pendente de aprovação' : 
                   statusFilter === 'PENDING_COMPLETION' ? 'Nenhum PDI pendente de conclusão' :
                   'Nenhum PDI encontrado' }}
              </h3>
              <p class="text-body-2 mt-2 text--secondary">
                {{ statusFilter === 'PENDING_APPROVAL' 
                  ? 'Não há PDIs aguardando sua aprovação no momento.' 
                  : statusFilter === 'PENDING_COMPLETION'
                  ? 'Não há PDIs com 100% das metas concluídas aguardando sua finalização.'
                  : 'Altere o filtro para ver outros PDIs.' }}
              </p>
            </v-card>

            <v-card v-if="loading" class="pa-8 text-center custom-card elevation-0">
              <v-progress-circular indeterminate color="#4FC3F7" size="64" />
              <p class="mt-4 text--secondary">Carregando PDIs...</p>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item>
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-card class="pa-4 custom-card" flat>
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <div class="text-caption text--secondary">Pendentes de Agendamento</div>
                  <div class="text-h3 orange--text font-weight-bold">
                    {{ pendingOneOnOnes.length }}
                  </div>
                </div>
                <v-icon size="50" color="orange">mdi-calendar-clock</v-icon>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card class="pa-4 custom-card" flat>
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <div class="text-caption text--secondary">Já Agendados</div>
                  <div class="text-h3 success--text font-weight-bold">
                    {{ scheduledOneOnOnes.length }}
                  </div>
                </div>
                <v-icon size="50" color="success">mdi-calendar-check</v-icon>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card class="pa-4 custom-card" flat>
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <div class="text-caption text--secondary">Cancelados</div>
                  <div class="text-h3 error--text font-weight-bold">
                    {{ cancelledOneOnOnes.length }}
                  </div>
                </div>
                <v-icon size="50" color="error">mdi-cancel</v-icon>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-chip-group v-model="oneOnOneFilter" mandatory active-class="cyan--text text--lighten-1" class="mb-4">
          <v-chip value="all" outlined class="filter-chip">
            <v-icon small left>mdi-format-list-bulleted</v-icon>
            Todos
          </v-chip>
          <v-chip value="pending" outlined class="filter-chip">
            <v-icon small left>mdi-calendar-clock</v-icon>
            Pendentes
          </v-chip>
          <v-chip value="scheduled" outlined class="filter-chip">
            <v-icon small left>mdi-calendar-check</v-icon>
            Agendados
          </v-chip>
          <v-chip value="cancelled" outlined class="filter-chip">
            <v-icon small left>mdi-cancel</v-icon>
            Cancelados
          </v-chip>
        </v-chip-group>

        <v-row>
          <v-col cols="12">
            <v-card
              v-for="meeting in filteredOneOnOnes"
              :key="meeting.id"
              class="mb-3 custom-card elevation-0"
              :class="getOneOnOneCardClass(meeting)"
            >
              <v-card-text class="pa-4">
                <v-row align="center">
                  <v-col cols="auto">
                    <v-avatar
                      :color="meeting.userAvatar ? 'transparent' : getAvatarColor(meeting.userName || 'User')"
                      size="50"
                    >
                      <v-img
                        v-if="meeting.userAvatar"
                        :src="meeting.userAvatar"
                        :alt="meeting.userName"
                      />
                      <span v-else class="white--text">
                        {{ getInitials(meeting.userName || 'U') }}
                      </span>
                    </v-avatar>
                  </v-col>

                  <v-col>
                    <div class="text-subtitle-1 font-weight-bold text--primary">{{ meeting.userName || 'Colaborador' }}</div>
                    <div class="text-caption text--secondary">{{ meeting.userJobRole || 'Cargo' }}</div>
                    <div class="text-body-2 mt-1 text--primary">{{ meeting.title }}</div>
                    
                    <v-chip
                      v-if="meeting.status === 'CANCELLED'"
                      x-small
                      color="error"
                      dark
                      outlined
                      class="mt-2"
                    >
                      <v-icon x-small left>mdi-cancel</v-icon>
                      Cancelado
                    </v-chip>
                    <v-chip
                      v-else-if="!meeting.scheduledAt"
                      x-small
                      color="orange"
                      dark
                      outlined
                      class="mt-2"
                    >
                      <v-icon x-small left>mdi-clock-outline</v-icon>
                      {{ translateIntentionPeriod(meeting.intentionPeriod) }}
                    </v-chip>
                    <v-chip
                      v-else
                      x-small
                      color="success"
                      dark
                      outlined
                      class="mt-2"
                    >
                      <v-icon x-small left>mdi-calendar</v-icon>
                      {{ formatDateTime(meeting.scheduledAt) }}
                    </v-chip>
                  </v-col>

                  <v-col cols="auto" class="d-flex align-center">
                    <v-btn
                      v-if="meeting.status !== 'CANCELLED' && !meeting.scheduledAt"
                      color="success"
                      dark
                      small
                      class="mr-2 text-none"
                      @click="openScheduleDialog(meeting)"
                    >
                      <v-icon small left>mdi-calendar-check</v-icon>
                      Agendar
                    </v-btn>
                    <v-btn
                      v-if="meeting.status !== 'CANCELLED'"
                      color="error"
                      small
                      outlined
                      class="mr-3 text-none"
                      @click="openCancelDialog(meeting)"
                    >
                      <v-icon small left>mdi-cancel</v-icon>
                      Cancelar
                    </v-btn>
                    <v-btn
                      icon
                      small
                      class="text--secondary"
                      @click="toggleExpandOneOnOne(meeting.id)"
                    >
                      <v-icon>
                        {{ expandedOneOnOne === meeting.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                      </v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-expand-transition>
                  <div v-show="expandedOneOnOne === meeting.id" class="mt-3 pt-3 divider-line">
                    <div v-if="meeting.topics" class="mb-3">
                      <div class="text-caption mb-1 text--secondary">Tópicos:</div>
                      <div class="text-body-2 text--primary">{{ meeting.topics }}</div>
                    </div>
                    <div v-if="meeting.notes" class="mb-3">
                      <div class="text-caption mb-1 text--secondary">Anotações:</div>
                      <div class="text-body-2 text--primary">{{ meeting.notes }}</div>
                    </div>
                    <div v-if="!meeting.topics && !meeting.notes" class="text-center py-3">
                      <v-icon class="empty-icon" size="32">mdi-text-box-outline</v-icon>
                      <p class="text-caption mt-2 mb-0 text--secondary">Sem anotações ou tópicos detalhados</p>
                    </div>
                  </div>
                </v-expand-transition>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card v-if="filteredOneOnOnes.length === 0 && !loadingOneOnOnes" class="pa-8 text-center custom-card elevation-0">
          <v-icon size="80" class="empty-icon">mdi-calendar-blank</v-icon>
          <h3 class="text-h6 text--primary mt-4">
            {{ oneOnOneFilter === 'pending' ? 'Nenhum One-on-One pendente' : 
               oneOnOneFilter === 'scheduled' ? 'Nenhum One-on-One agendado' : 
               oneOnOneFilter === 'cancelled' ? 'Nenhum One-on-One cancelado' :
               'Nenhum One-on-One encontrado' }}
          </h3>
          <p class="text-body-2 mt-2 text--secondary">
            {{ oneOnOneFilter === 'pending' ? 'Não há reuniões 1:1 aguardando agendamento no momento.' : 
               oneOnOneFilter === 'scheduled' ? 'Não há reuniões 1:1 já agendadas.' : 
               oneOnOneFilter === 'cancelled' ? 'Não há reuniões 1:1 canceladas.' :
               'Altere o filtro para ver outras reuniões.' }}
          </p>
        </v-card>

        <v-card v-if="loadingOneOnOnes" class="pa-8 text-center custom-card elevation-0">
          <v-progress-circular indeterminate color="#4FC3F7" size="64" />
          <p class="mt-4 text--secondary">Carregando One-on-Ones...</p>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="scheduleDialog" max-width="600px" persistent>
      <v-card class="custom-card">
        <div class="d-flex align-center pa-4 custom-header">
          <v-icon color="success" class="mr-3">mdi-calendar-check</v-icon>
          <span class="text-h6 font-weight-bold text--primary">Definir Data e Horário</span>
        </div>

        <v-card-text class="pt-6">
          <div v-if="selectedOneOnOne" class="mb-5">
            <div class="text-subtitle-2 mb-2 text--secondary">Colaborador</div>
            <div class="d-flex align-center mb-4">
              <v-avatar :color="getAvatarColor(selectedOneOnOne.userName || 'User')" size="40" class="mr-3">
                <span class="white--text">{{ getInitials(selectedOneOnOne.userName || 'U') }}</span>
              </v-avatar>
              <div>
                <div class="text-body-2 font-weight-medium text--primary">{{ selectedOneOnOne.userName }}</div>
                <div class="text-caption text--secondary">{{ selectedOneOnOne.userJobRole }}</div>
              </div>
            </div>

            <div class="text-subtitle-2 mb-2 text--secondary">Título</div>
            <div class="text-body-2 mb-4 text--primary">{{ selectedOneOnOne.title }}</div>

            <div v-if="selectedOneOnOne.notes" class="mb-4">
              <div class="text-subtitle-2 mb-2 text--secondary">Anotações</div>
              <div class="text-body-2 pa-3 rounded" style="background: rgba(76, 175, 80, 0.1); border-left: 3px solid #4CAF50;">
                <span class="text--primary">{{ selectedOneOnOne.notes }}</span>
              </div>
            </div>

            <div class="text-subtitle-2 mb-2 text--secondary">Intenção de Período</div>
            <v-chip small color="orange" dark outlined class="mb-4">
              {{ translateIntentionPeriod(selectedOneOnOne.intentionPeriod) }}
            </v-chip>
          </div>

          <v-form ref="scheduleForm" v-model="validScheduleForm">
            <v-row>
              <v-col cols="12" md="6">
                <v-menu
                  v-model="scheduleDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="scheduleData.date"
                      label="Data *"
                      :rules="[v => !!v || 'Data é obrigatória']"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="scheduleData.date"
                    :min="today"
                    color="success"
                    no-title
                    @input="scheduleDateMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="scheduleData.time"
                  label="Horário *"
                  type="time"
                  :rules="[v => !!v || 'Horário é obrigatório']"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-0">
          <v-spacer />
          <v-btn text class="text-none cancel-btn" @click="closeScheduleDialog">
            Cancelar
          </v-btn>
          <v-btn color="success" dark class="text-none" :loading="savingOneOnOne" @click="confirmSchedule">
            <v-icon left>mdi-check</v-icon> Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="500px" persistent>
      <v-card class="custom-card">
        <div class="d-flex align-center pa-4 custom-header">
          <v-icon color="error" class="mr-3">mdi-cancel</v-icon>
          <span class="text-h6 font-weight-bold text--primary">Cancelar One-on-One</span>
        </div>

        <v-card-text class="pt-6">
          <v-alert type="warning" outlined dense class="mb-4">
            Você está prestes a cancelar o One-on-One <strong>{{ selectedOneOnOneToCancel?.title }}</strong>
            com <strong>{{ selectedOneOnOneToCancel?.userName }}</strong>.
          </v-alert>
          <v-form ref="cancelForm" v-model="validCancelForm">
            <v-textarea
              v-model="cancelReason"
              label="Motivo do Cancelamento *"
              :rules="[v => !!v || 'Motivo é obrigatório']"
              outlined
              rows="4"
              placeholder="Ex: Conflito de agenda..."
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-0">
          <v-spacer />
          <v-btn text class="text-none cancel-btn" @click="closeCancelDialog">Voltar</v-btn>
          <v-btn color="error" dark class="text-none" :loading="savingOneOnOne" :disabled="!validCancelForm" @click="cancelOneOnOne">
            Confirmar Cancelamento
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="approveDialog" max-width="500px" persistent>
      <v-card class="custom-card">
        <div class="d-flex align-center pa-4 custom-header">
          <v-icon color="success" class="mr-3">mdi-check-circle</v-icon>
          <span class="text-h6 font-weight-bold text--primary">Aprovar PDI</span>
        </div>

        <v-card-text class="pt-6">
          <v-alert type="success" outlined dense class="mb-4">
            Aprovar o PDI: <strong>{{ selectedPdi?.title }}</strong>
          </v-alert>
          <p class="text-body-2 text--secondary">
            Ao aprovar este PDI, o colaborador poderá iniciar o acompanhamento e execução das ações planejadas.
          </p>
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-0">
          <v-spacer />
          <v-btn text class="text-none cancel-btn" @click="closeApproveDialog" :disabled="saving">
            Cancelar
          </v-btn>
          <v-btn color="success" dark class="text-none" @click="approvePdi" :loading="saving">
            Confirmar Aprovação
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="500px" persistent>
      <v-card class="custom-card">
        <div class="d-flex align-center pa-4 custom-header">
          <v-icon color="error" class="mr-3">mdi-close-circle</v-icon>
          <span class="text-h6 font-weight-bold text--primary">Rejeitar PDI</span>
        </div>

        <v-card-text class="pt-6">
          <v-alert type="warning" outlined dense class="mb-4">
            Rejeitar o PDI: <strong>{{ selectedPdi?.title }}</strong>
          </v-alert>
          <v-textarea
            v-model="rejectionReason"
            label="Motivo da Rejeição *"
            :rules="[v => !!v || 'Motivo é obrigatório']"
            outlined
            rows="4"
            counter
          />
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-0">
          <v-spacer />
          <v-btn text class="text-none cancel-btn" @click="closeRejectDialog" :disabled="saving">
            Cancelar
          </v-btn>
          <v-btn color="error" dark class="text-none" @click="rejectPdi" :loading="saving" :disabled="!rejectionReason || rejectionReason.trim() === ''">
            Confirmar Rejeição
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="completePdiDialog" max-width="500px" persistent>
      <v-card class="custom-card">
        <div class="d-flex align-center pa-4 custom-header">
          <v-icon color="#4FC3F7" class="mr-3">mdi-check-all</v-icon>
          <span class="text-h6 font-weight-bold text--primary">Concluir PDI</span>
        </div>

        <v-card-text class="pt-6">
          <v-alert type="success" outlined dense class="mb-4">
            <strong>{{ selectedPdi?.userName }}</strong> concluiu todas as metas de <strong>{{ selectedPdi?.title }}</strong>.
          </v-alert>
          
          <v-alert color="rgba(79, 195, 247, 0.1)" dense class="border-none mb-4">
            <div class="d-flex align-center" style="color: #00B0FF;">
              <v-icon color="#00B0FF" class="mr-2" small>mdi-trophy</v-icon>
              <span><strong>Recompensa:</strong> +{{ selectedPdi?.xpReward || 500 }} XP</span>
            </div>
          </v-alert>

          <p class="text-subtitle-2 font-weight-bold mb-2 text--primary">Como deseja prosseguir?</p>
          <p class="text-caption text--secondary">
            Você pode apenas concluir o PDI ou agendar um One-on-One automático para discutir os resultados.
          </p>
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-0 flex-wrap">
          <v-spacer />
          <v-btn text class="text-none mb-2 cancel-btn" @click="closeCompletePdiDialog" :disabled="saving">
            Cancelar
          </v-btn>
          <v-btn outlined class="text-none mb-2 cancel-btn" @click="completePdiOnly" :loading="saving">
            Apenas Concluir
          </v-btn>
          <v-btn color="#4FC3F7" dark class="text-none mb-2" @click="completePdiAndSchedule" :disabled="saving">
            Concluir e Agendar 1:1
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="scheduleOneOnOneDialog" max-width="600px" persistent>
      <v-card class="custom-card" rounded="lg">
        <div class="d-flex align-center justify-space-between pa-5 pb-3 custom-header">
          <div class="d-flex align-center">
            <v-icon color="#4FC3F7" size="24" class="mr-3">mdi-calendar-text</v-icon>
            <div>
              <div class="text-subtitle-1 font-weight-bold text--primary" style="line-height: 1.3">Agendar One-on-One</div>
              <div class="text-caption text--secondary">Avaliação do PDI concluído</div>
            </div>
          </div>
          <v-btn icon small @click="closeScheduleOneOnOneDialog" class="text--secondary">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-5" style="max-height: 65vh; overflow-y: auto">
          <div class="mb-5 pa-4 rounded-lg" style="background-color: rgba(79, 195, 247, 0.05); border: 1px solid rgba(79, 195, 247, 0.2);">
            <div class="d-flex align-center mb-2">
              <v-icon small color="#00B0FF" class="mr-2">mdi-information-outline</v-icon>
              <span class="text-caption font-weight-bold text-uppercase" style="color: #00B0FF; letter-spacing: 0.5px">
                PDI Concluído
              </span>
            </div>
            <div class="mb-2">
              <span class="text-caption text--secondary">Colaborador:</span>
              <div class="text-body-2 font-weight-medium text--primary">{{ selectedPdi?.userName }}</div>
            </div>
            <div>
              <span class="text-caption text--secondary">PDI:</span>
              <div class="text-body-2 text--primary">{{ selectedPdi?.title }}</div>
            </div>
          </div>

          <v-form ref="scheduleOneOnOneForm" v-model="validScheduleOneOnOneForm">
            <v-row dense class="mb-5">
              <v-col cols="4">
                <div class="text-caption font-weight-medium text-uppercase mb-2 text--secondary" style="letter-spacing: 0.5px">Data</div>
                <v-text-field
                  v-model="newMeeting.date"
                  type="date"
                  :min="today"
                  :rules="[v => !!v || 'Data obrigatória']"
                  outlined dense rounded hide-details="auto"
                  prepend-inner-icon="mdi-calendar"
                />
              </v-col>
              <v-col cols="4">
                <div class="text-caption font-weight-medium text-uppercase mb-2 text--secondary" style="letter-spacing: 0.5px">Horário</div>
                <v-select
                  v-model="newMeeting.time"
                  :items="timeOptions"
                  :rules="[v => !!v || 'Horário obrigatório']"
                  outlined dense rounded hide-details="auto"
                />
              </v-col>
              <v-col cols="4">
                <div class="text-caption font-weight-medium text-uppercase mb-2 text--secondary" style="letter-spacing: 0.5px">Duração</div>
                <v-select
                  v-model="newMeeting.duration"
                  :items="durationOptions"
                  outlined dense rounded hide-details="auto"
                  prepend-inner-icon="mdi-clock-outline"
                />
              </v-col>
            </v-row>

            <div class="d-flex align-center mb-2">
              <div class="text-caption font-weight-medium text-uppercase text--secondary" style="letter-spacing: 0.5px">Tópicos da pauta</div>
              <v-btn
                x-small text rounded class="text-none ml-2 cancel-btn"
                :color="showSuggestions ? '#00B0FF' : ''"
                @click="showSuggestions = !showSuggestions"
              >
                <v-icon x-small left>mdi-auto-fix</v-icon> Sugestões
              </v-btn>
            </div>

            <div v-if="showSuggestions" class="d-flex flex-wrap mb-4" style="gap: 8px">
              <v-chip
                v-for="topic in suggestedTopics"
                :key="topic"
                :color="newMeeting.selectedTopics.includes(topic) ? '#4FC3F7' : ''"
                :dark="newMeeting.selectedTopics.includes(topic)"
                :outlined="!newMeeting.selectedTopics.includes(topic)"
                small
                style="cursor: pointer;"
                class="suggestion-chip"
                @click="toggleTopic(topic)"
              >
                {{ topic }}
              </v-chip>
            </div>

            <div class="d-flex align-center mb-4" style="gap: 8px">
              <v-text-field
                v-model="customTopic"
                placeholder="Adicionar tópico personalizado..."
                outlined dense rounded hide-details
                class="flex-grow-1"
                @keydown.enter.prevent="addCustomTopic"
              />
              <v-btn
                icon small color="#00B0FF" outlined
                style="border-radius: 50%;"
                :disabled="!customTopic.trim()"
                @click="addCustomTopic"
              >
                <v-icon size="18">mdi-plus</v-icon>
              </v-btn>
            </div>

            <div v-if="newMeeting.customTopics.length" class="d-flex flex-wrap mb-5" style="gap: 8px">
              <v-chip
                v-for="(topic, i) in newMeeting.customTopics"
                :key="'custom-' + i"
                small close color="#4FC3F7" dark
                @click:close="removeCustomTopic(i)"
              >
                {{ topic }}
              </v-chip>
            </div>

            <div class="text-caption font-weight-medium text-uppercase mb-2 text--secondary" style="letter-spacing: 0.5px">Notas prévias</div>
            <v-textarea
              v-model="newMeeting.notes"
              placeholder="Adicione contexto ou pontos importantes..."
              outlined rounded rows="3" hide-details="auto"
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-5 custom-header" style="border-bottom: none;">
          <v-spacer />
          <v-btn rounded text class="text-none px-5 cancel-btn" @click="closeScheduleOneOnOneDialog">
            Cancelar
          </v-btn>
          <v-btn
            color="#4FC3F7" dark rounded depressed class="text-none px-6 ml-3"
            :loading="savingOneOnOneMeeting"
            :disabled="!validScheduleOneOnOneForm"
            @click="scheduleMeetingAndCompletePdi"
          >
            <v-icon left size="18">mdi-calendar-check</v-icon> Agendar e Concluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false" class="white--text">Fechar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { PdisService, OneOnOnesService } from '@/services'

export default {
  name: 'Approval',

  data: () => ({
    // Abas
    activeTab: 0,
    
    // PDIs
    loading: false,
    pdis: [],
    expandedPdis: [],
    statusFilter: 'PENDING_APPROVAL',
    
    // Dialog de Rejeição PDI
    rejectDialog: false,
    approveDialog: false,
    completePdiDialog: false,
    selectedPdi: null,
    rejectionReason: '',
    saving: null, // ID do PDI sendo salvo
    
    // One-on-Ones
    loadingOneOnOnes: false,
    oneOnOnes: [],
    expandedOneOnOne: null,
    oneOnOneFilter: 'pending',
    
    // Dialog de Agendamento One-on-One
    scheduleDialog: false,
    selectedOneOnOne: null,
    validScheduleForm: false,
    savingOneOnOne: false,
    scheduleDateMenu: false,
    scheduleData: {
      date: '',
      time: '',
    },
    
    // Dialog de Agendamento One-on-One (PDI)
    scheduleOneOnOneDialog: false,
    validScheduleOneOnOneForm: false,
    showSuggestions: true,
    customTopic: '',
    savingOneOnOneMeeting: false,
    suggestedTopics: [
      'Avaliação do PDI concluído',
      'Feedback sobre metas alcançadas',
      'Próximos objetivos',
      'Desenvolvimento de carreira',
      'Desafios enfrentados',
      'Pontos de melhoria',
      'Reconhecimento e conquistas',
      'Planejamento futuro',
    ],
    newMeeting: {
      meetingType: 'mentor',
      userId: null,
      date: '',
      time: '14:00',
      duration: 30,
      selectedTopics: [],
      customTopics: [],
      notes: '',
    },
    defaultMeeting: {
      meetingType: 'mentor',
      userId: null,
      date: '',
      time: '14:00',
      duration: 30,
      selectedTopics: [],
      customTopics: [],
      notes: '',
    },
    timeOptions: [
      { text: '08:00', value: '08:00' },
      { text: '08:30', value: '08:30' },
      { text: '09:00', value: '09:00' },
      { text: '09:30', value: '09:30' },
      { text: '10:00', value: '10:00' },
      { text: '10:30', value: '10:30' },
      { text: '11:00', value: '11:00' },
      { text: '11:30', value: '11:30' },
      { text: '12:00', value: '12:00' },
      { text: '13:00', value: '13:00' },
      { text: '13:30', value: '13:30' },
      { text: '14:00', value: '14:00' },
      { text: '14:30', value: '14:30' },
      { text: '15:00', value: '15:00' },
      { text: '15:30', value: '15:30' },
      { text: '16:00', value: '16:00' },
      { text: '16:30', value: '16:30' },
      { text: '17:00', value: '17:00' },
      { text: '17:30', value: '17:30' },
      { text: '18:00', value: '18:00' },
    ],
    durationOptions: [
      { text: '15 min', value: 15 },
      { text: '30 min', value: 30 },
      { text: '45 min', value: 45 },
      { text: '60 min', value: 60 },
      { text: '90 min', value: 90 },
    ],
    
    // Dialog de Cancelamento One-on-One
    cancelDialog: false,
    selectedOneOnOneToCancel: null,
    cancelReason: '',
    validCancelForm: false,
    
    // Snackbar
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),

  computed: {
    filteredPdis() {
      const approvalStatuses = ['PENDING_APPROVAL', 'APPROVED', 'REJECTED']
      let filtered = this.pdis.filter(pdi => 
        approvalStatuses.includes(pdi.status) || 
        (pdi.status === 'IN_PROGRESS' && (pdi.progress >= 100))
      )
      
      if (this.statusFilter === 'PENDING_COMPLETION') {
        filtered = filtered.filter(pdi => pdi.status === 'IN_PROGRESS' && pdi.progress >= 100)
      } else if (this.statusFilter !== 'all') {
        filtered = filtered.filter(pdi => pdi.status === this.statusFilter)
      }
      
      return filtered
    },

    pendingCount() {
      return this.pdis.filter(p => p.status === 'PENDING_APPROVAL').length
    },

    pendingCompletionCount() {
      return this.pdis.filter(p => p.status === 'IN_PROGRESS' && p.progress >= 100).length
    },

    approvedCount() {
      return this.pdis.filter(p => p.status === 'APPROVED').length
    },

    rejectedCount() {
      return this.pdis.filter(p => p.status === 'REJECTED').length
    },

    today() {
      return new Date().toISOString().split('T')[0]
    },

    pendingOneOnOnes() {
      return this.oneOnOnes.filter(m => !m.scheduledAt && m.status !== 'CANCELLED')
    },

    scheduledOneOnOnes() {
      return this.oneOnOnes.filter(m => m.scheduledAt && m.status !== 'CANCELLED')
    },

    cancelledOneOnOnes() {
      return this.oneOnOnes.filter(m => m.status === 'CANCELLED')
    },

    filteredOneOnOnes() {
      if (this.oneOnOneFilter === 'all') {
        return this.oneOnOnes
      } else if (this.oneOnOneFilter === 'pending') {
        return this.oneOnOnes.filter(m => !m.scheduledAt && m.status !== 'CANCELLED')
      } else if (this.oneOnOneFilter === 'scheduled') {
        return this.oneOnOnes.filter(m => m.scheduledAt && m.status !== 'CANCELLED')
      } else if (this.oneOnOneFilter === 'cancelled') {
        return this.oneOnOnes.filter(m => m.status === 'CANCELLED')
      }
      return this.oneOnOnes
    },
  },

  mounted() {
    const tabParam = this.$route.query.tab
    if (tabParam !== undefined) {
      const tabIndex = parseInt(tabParam, 10)
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 1) {
        this.activeTab = tabIndex
      }
    }
    
    const statusParam = this.$route.query.status
    if (statusParam) {
      const validStatuses = ['all', 'PENDING_APPROVAL', 'PENDING_COMPLETION', 'APPROVED', 'REJECTED']
      if (validStatuses.includes(statusParam)) {
        this.statusFilter = statusParam
      }
    }
    
    this.loadPdis()
    this.loadOneOnOnes()
  },

  methods: {
    async loadPdis() {
      this.loading = true
      try {
        const response = await PdisService.list()
        this.pdis = response.data
      } catch (error) {
        console.error('Erro ao carregar PDIs:', error)
        this.showSnackbar('Erro ao carregar PDIs', 'error')
      } finally {
        this.loading = false
      }
    },

    toggleExpand(pdiId) {
      const index = this.expandedPdis.indexOf(pdiId)
      if (index > -1) {
        this.expandedPdis.splice(index, 1)
      } else {
        this.expandedPdis.push(pdiId)
      }
    },

    openApproveDialog(pdi) {
      this.selectedPdi = pdi
      this.approveDialog = true
    },

    closeApproveDialog() {
      this.approveDialog = false
      this.selectedPdi = null
    },

    async approvePdi() {
      if (!this.selectedPdi) return

      this.saving = this.selectedPdi.id
      try {
        await PdisService.updateStatus(this.selectedPdi.id, 'APPROVED')
        this.showSnackbar('PDI aprovado com sucesso!', 'success')
        this.closeApproveDialog()
        this.loadPdis()
      } catch (error) {
        console.error('Erro ao aprovar PDI:', error)
        this.showSnackbar('Erro ao aprovar PDI', 'error')
      } finally {
        this.saving = null
      }
    },

    openRejectDialog(pdi) {
      this.selectedPdi = pdi
      this.rejectionReason = ''
      this.rejectDialog = true
    },

    closeRejectDialog() {
      this.rejectDialog = false
      this.selectedPdi = null
      this.rejectionReason = ''
    },

    openCompletePdiDialog(pdi) {
      this.selectedPdi = pdi
      this.completePdiDialog = true
    },

    closeCompletePdiDialog() {
      this.completePdiDialog = false
      this.selectedPdi = null
    },

    async completePdiOnly() {
      if (!this.selectedPdi) return

      this.saving = this.selectedPdi.id
      try {
        await PdisService.updateStatus(this.selectedPdi.id, 'COMPLETED')
        this.showSnackbar('PDI concluído com sucesso!', 'success')
        this.closeCompletePdiDialog()
        this.loadPdis()
      } catch (error) {
        console.error('Erro ao concluir PDI:', error)
        this.showSnackbar('Erro ao concluir PDI', 'error')
      } finally {
        this.saving = null
      }
    },

    completePdiAndSchedule() {
      this.completePdiDialog = false
      this.openScheduleOneOnOneDialog()
    },

    openScheduleOneOnOneDialog() {
      this.newMeeting = {
        ...this.defaultMeeting,
        selectedTopics: ['Avaliação do PDI concluído'],
        customTopics: [],
        userId: this.selectedPdi?.userId,
        notes: `Reunião de avaliação do PDI concluído: ${this.selectedPdi?.title}`,
      }
      this.customTopic = ''
      this.showSuggestions = true
      
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      this.newMeeting.date = tomorrow.toISOString().split('T')[0]
      this.newMeeting.time = '14:00'
      this.newMeeting.duration = 30
      
      this.scheduleOneOnOneDialog = true
    },

    closeScheduleOneOnOneDialog() {
      try {
        if (this.$refs.scheduleOneOnOneForm) {
          this.$refs.scheduleOneOnOneForm.resetValidation()
        }
      } catch (e) {
        // ignore
      }
      this.customTopic = ''
      this.scheduleOneOnOneDialog = false
      this.selectedPdi = null
    },

    toggleTopic(topic) {
      const idx = this.newMeeting.selectedTopics.indexOf(topic)
      if (idx >= 0) {
        this.newMeeting.selectedTopics.splice(idx, 1)
      } else {
        this.newMeeting.selectedTopics.push(topic)
      }
    },

    addCustomTopic() {
      const topic = this.customTopic.trim()
      if (topic && !this.newMeeting.customTopics.includes(topic)) {
        this.newMeeting.customTopics.push(topic)
      }
      this.customTopic = ''
    },

    removeCustomTopic(index) {
      this.newMeeting.customTopics.splice(index, 1)
    },

    async scheduleMeetingAndCompletePdi() {
      if (!this.$refs.scheduleOneOnOneForm.validate()) return
      if (!this.selectedPdi) return

      this.savingOneOnOneMeeting = true
      try {
        await PdisService.updateStatus(this.selectedPdi.id, 'COMPLETED')
        
        const scheduledAt = `${this.newMeeting.date}T${this.newMeeting.time}:00`
        const allTopics = [...this.newMeeting.selectedTopics, ...this.newMeeting.customTopics]
        
        const meetingData = {
          meetingType: 'mentor',
          userId: this.selectedPdi.userId,
          scheduledAt,
          status: 'SCHEDULED_RESULT',
          topics: allTopics,
          notes: this.newMeeting.notes,
          duration: this.newMeeting.duration,
        }
        
        await OneOnOnesService.schedule(meetingData)
        this.showSnackbar(`PDI concluído e One-on-One agendada para ${this.formatDate(this.newMeeting.date)}!`, 'success')
        
        this.closeScheduleOneOnOneDialog()
        this.loadPdis()
      } catch (error) {
        console.error('Erro ao concluir PDI e agendar reunião:', error)
        this.showSnackbar('Erro ao processar solicitação', 'error')
      } finally {
        this.savingOneOnOneMeeting = false
      }
    },

    async completePdi() {
      if (!this.selectedPdi) return

      this.saving = this.selectedPdi.id
      try {
        await PdisService.updateStatus(this.selectedPdi.id, 'COMPLETED')
        this.showSnackbar('PDI concluído com sucesso!', 'success')
        this.closeCompletePdiDialog()
        this.loadPdis()
      } catch (error) {
        console.error('Erro ao concluir PDI:', error)
        this.showSnackbar('Erro ao concluir PDI', 'error')
      } finally {
        this.saving = null
      }
    },

    async rejectPdi() {
      if (!this.selectedPdi || !this.rejectionReason) return

      this.saving = this.selectedPdi.id
      try {
        await PdisService.updateStatus(this.selectedPdi.id, 'REJECTED', this.rejectionReason)
        this.showSnackbar('PDI rejeitado', 'warning')
        this.closeRejectDialog()
        this.loadPdis()
      } catch (error) {
        console.error('Erro ao rejeitar PDI:', error)
        this.showSnackbar('Erro ao rejeitar PDI', 'error')
      } finally {
        this.saving = null
      }
    },

    getStatusColor(status) {
      const colors = {
        PENDING_APPROVAL: 'orange',
        APPROVED: 'success',
        REJECTED: 'error',
        CANCELLED: 'grey',
        IN_PROGRESS: '#4FC3F7',
      }
      return colors[status] || 'grey'
    },

    getStatusIcon(status) {
      const icons = {
        PENDING_APPROVAL: 'mdi-clock-alert',
        APPROVED: 'mdi-check-circle',
        REJECTED: 'mdi-close-circle',
        CANCELLED: 'mdi-cancel',
        IN_PROGRESS: 'mdi-progress-clock',
      }
      return icons[status] || 'mdi-file'
    },

    getStatusLabel(status) {
      const labels = {
        PENDING_APPROVAL: 'Aguardando Aprovação',
        APPROVED: 'Aprovado',
        REJECTED: 'Rejeitado',
        CANCELLED: 'Cancelado',
        IN_PROGRESS: 'Em Progresso',
      }
      return labels[status] || status
    },

    getPdiCardClass(status, progress) {
      if (status === 'IN_PROGRESS' && progress >= 100) return 'border-completion'
      if (status === 'APPROVED') return 'border-success'
      if (status === 'REJECTED') return 'border-error'
      if (status === 'PENDING_APPROVAL') return 'border-warning'
      if (status === 'CANCELLED') return 'border-grey'
      return ''
    },

    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      return d.toLocaleDateString('pt-BR')
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      const d = new Date(dateTime)
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // ---------- One-on-One Methods ----------

    async loadOneOnOnes() {
      this.loadingOneOnOnes = true
      try {
        const response = await OneOnOnesService.list({ role: 'MANAGER' })
        this.oneOnOnes = response.data
      } catch (error) {
        console.error('Erro ao carregar One-on-Ones:', error)
        this.showSnackbar('Erro ao carregar One-on-Ones', 'error')
      } finally {
        this.loadingOneOnOnes = false
      }
    },

    toggleExpandOneOnOne(meetingId) {
      if (this.expandedOneOnOne === meetingId) {
        this.expandedOneOnOne = null
      } else {
        this.expandedOneOnOne = meetingId
      }
    },

    openScheduleDialog(meeting) {
      this.selectedOneOnOne = meeting
      const today = new Date()
      let suggestedDate = new Date()
      
      switch (meeting.intentionPeriod) {
        case 'TODAY':
          suggestedDate = today
          break
        case 'THIS_WEEK':
          const dayOfWeek = today.getDay()
          const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek
          suggestedDate.setDate(today.getDate() + daysUntilMonday)
          break
        case 'THIS_MONTH':
          suggestedDate.setMonth(today.getMonth() + 1, 1)
          const firstDayOfMonth = suggestedDate.getDay()
          const daysToMonday = firstDayOfMonth === 0 ? 1 : (8 - firstDayOfMonth) % 7
          suggestedDate.setDate(1 + daysToMonday)
          break
        default:
          suggestedDate.setDate(today.getDate() + 1)
      }
      
      this.scheduleData.date = suggestedDate.toISOString().split('T')[0]
      this.scheduleData.time = '14:00'
      this.scheduleDialog = true
    },

    closeScheduleDialog() {
      this.scheduleDialog = false
      this.selectedOneOnOne = null
      this.scheduleData = { date: '', time: '' }
      this.$refs.scheduleForm?.reset()
    },

    async confirmSchedule() {
      if (!this.$refs.scheduleForm.validate()) return

      this.savingOneOnOne = true
      try {
        const scheduledAt = `${this.scheduleData.date}T${this.scheduleData.time}:00`
        await OneOnOnesService.updateSchedule(this.selectedOneOnOne.id, scheduledAt)
        
        this.showSnackbar('Agendamento confirmado com sucesso!', 'success')
        this.closeScheduleDialog()
        this.loadOneOnOnes()
      } catch (error) {
        console.error('Erro ao confirmar agendamento:', error)
        this.showSnackbar('Erro ao confirmar agendamento', 'error')
      } finally {
        this.savingOneOnOne = false
      }
    },

    openCancelDialog(meeting) {
      this.selectedOneOnOneToCancel = meeting
      this.cancelReason = ''
      this.cancelDialog = true
    },

    closeCancelDialog() {
      this.cancelDialog = false
      this.selectedOneOnOneToCancel = null
      this.cancelReason = ''
      this.$refs.cancelForm?.reset()
    },

    async cancelOneOnOne() {
      if (!this.$refs.cancelForm.validate()) return

      this.savingOneOnOne = true
      try {
        await OneOnOnesService.cancel(this.selectedOneOnOneToCancel.id, this.cancelReason)
        this.showSnackbar('One-on-One cancelado com sucesso', 'warning')
        this.closeCancelDialog()
        this.loadOneOnOnes()
      } catch (error) {
        console.error('Erro ao cancelar One-on-One:', error)
        this.showSnackbar('Erro ao cancelar One-on-One', 'error')
      } finally {
        this.savingOneOnOne = false
      }
    },

    getOneOnOneCardClass(meeting) {
      if (meeting.status === 'CANCELLED') return 'border-error'
      if (!meeting.scheduledAt) return 'border-warning'
      return 'border-success'
    },

    translateIntentionPeriod(intentionPeriod) {
      const translations = {
        'TODAY': 'Hoje',
        'THIS_WEEK': 'Esta semana',
        'THIS_MONTH': 'Este mês'
      }
      return translations[intentionPeriod] || intentionPeriod
    },

    getAvatarColor(name) {
      const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2']
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },

    getInitials(name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
  },
}
</script>

<style scoped>
/* Fundo Global Premium */
.dashboard-container {
  background-color: var(--bg-main, #0D0D0D) !important;
  max-width: 1440px;
  margin: 0 auto;
}

/* ========== Cores Essenciais de Borda Cards ========== */
.border-success { border-left: 4px solid #4caf50 !important; }
.border-error { border-left: 4px solid #f44336 !important; }
.border-warning { border-left: 4px solid #ff9800 !important; }
.border-completion { border-left: 4px solid #4FC3F7 !important; }
.border-grey { border-left: 4px solid #9e9e9e !important; }
.border-none { border: none !important; }

/* ========== Componentes Adaptáveis (Dark/Light) ========== */
.custom-card {
  transition: background-color 0.3s ease;
}

.theme--dark .custom-card {
  background-color: #1E1E22 !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.theme--light .custom-card {
  background-color: #FFFFFF !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.theme--dark .custom-header { border-bottom: 1px solid rgba(255, 255, 255, 0.05); }
.theme--light .custom-header { border-bottom: 1px solid rgba(0, 0, 0, 0.1); }

.theme--dark .info-box {
  background-color: rgba(255,255,255,0.03) !important;
  border: 1px solid rgba(255,255,255,0.08) !important;
}
.theme--light .info-box {
  background-color: rgba(0,0,0,0.02) !important;
  border: 1px solid rgba(0,0,0,0.06) !important;
}

.theme--dark .divider-line { border-top: 1px solid rgba(255, 255, 255, 0.05); }
.theme--light .divider-line { border-top: 1px solid rgba(0, 0, 0, 0.1); }

/* ========== Abas ========== */
.approval-tabs ::v-deep .v-tab {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500;
}
/* Aplica a cor cinza apenas nas abas INATIVAS */
.theme--dark .approval-tabs ::v-deep .v-tab:not(.v-tab--active) { color: rgba(255, 255, 255, 0.55) !important; }
.theme--light .approval-tabs ::v-deep .v-tab:not(.v-tab--active) { color: rgba(0, 0, 0, 0.55) !important; }

/* Força a cor ciano na aba ATIVA sobrepondo o tema */
.approval-tabs ::v-deep .v-tab--active,
.theme--dark .approval-tabs ::v-deep .v-tab.v-tab--active,
.theme--light .approval-tabs ::v-deep .v-tab.v-tab--active {
  color: #4FC3F7 !important;
}

.approval-tabs ::v-deep .v-tabs-slider-wrapper .v-tabs-slider {
  background-color: #4FC3F7 !important;
}

/* ========== Chips e Ícones Variáveis ========== */
.theme--dark .filter-chip { border-color: rgba(255,255,255,0.2) !important; color: rgba(255,255,255,0.7) !important; }
.theme--light .filter-chip { border-color: rgba(0,0,0,0.2) !important; color: rgba(0,0,0,0.7) !important; }

.theme--dark .category-chip { border-color: rgba(255,255,255,0.4) !important; color: #FFFFFF !important; }
.theme--light .category-chip { border-color: rgba(0,0,0,0.4) !important; color: #000000 !important; }

.theme--dark .suggestion-chip:not(.v-chip--active) { border-color: rgba(255,255,255,0.1) !important; color: rgba(255,255,255,0.7) !important;}
.theme--light .suggestion-chip:not(.v-chip--active) { border-color: rgba(0,0,0,0.2) !important; color: rgba(0,0,0,0.7) !important;}

.theme--dark .empty-icon { color: rgba(255,255,255,0.1) !important; }
.theme--light .empty-icon { color: rgba(0,0,0,0.1) !important; }

.theme--dark .cancel-btn { color: rgba(255,255,255,0.55) !important; }
.theme--light .cancel-btn { color: rgba(0,0,0,0.6) !important; }

/* ========== Overrides Globais ========== */
::v-deep .theme--dark.v-text-field--outlined > .v-input__control > .v-input__slot fieldset {
  border-color: rgba(255, 255, 255, 0.15);
}

::v-deep .theme--dark.v-btn.v-btn--outlined {
  border-color: rgba(255, 255, 255, 0.2);
}

::v-deep .theme--dark .v-date-picker-header .v-btn {
  color: rgba(255, 255, 255, 0.87) !important;
}
</style>